import { useEffect } from "react";
import useSWR from "swr";
import { useUserPreferences } from "~/hooks/useUserPreferences";

export interface UsePinnedListOptions<T> {
  /** Sub-key under `favoriteItems`, e.g. `"agent-favorites"`. */
  favoritesKey: string;
  /** Batch-resolve ids to records via the resource's `by-ids` endpoint. */
  fetchByIds: (ids: string[]) => Promise<T[]>;
  getId: (item: T) => string;
}

export interface UsePinnedListResult<T> {
  /** Resolved pinned records, in stored order, viewable subset only. */
  pinned: T[];
  /** Ids of the resolved pinned records — for de-duping against a browse list. */
  pinnedIds: Set<string>;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  error: unknown;
}

/**
 * The pinned (favorited) records for a resource, resolved independently of the
 * browse list via the resource's `by-ids` endpoint. Favorite ids live in
 * localStorage (`favoriteItems[favoritesKey]`), so a pinned record surfaces even
 * when it falls outside the current page or search.
 *
 * Self-heals: `by-ids` returns only the viewable, non-deleted subset, so an id
 * missing from a *successful* response is pruned from storage. A failed fetch
 * prunes nothing, so a transient error never drops a favorite.
 *
 * `isFavorite`/`toggleFavorite` accept any id, so one pair drives the star on
 * both the pinned section and the browse list.
 */
export function usePinnedList<T>({
  favoritesKey,
  fetchByIds,
  getId,
}: UsePinnedListOptions<T>): UsePinnedListResult<T> {
  const [preferences, updatePreferences] = useUserPreferences();
  const ids = preferences.favoriteItems?.[favoritesKey] ?? [];
  const favoriteSet = new Set(ids);

  // Key on the id set (order-independent) so the fetch revalidates when a
  // favorite is added or removed, and dedupes across mounted consumers.
  // `keepPreviousData` serves the prior records while the new key refetches, so
  // toggling a favorite doesn't blank the section (and drop browse deduping).
  const key = ids.length > 0 ? [favoritesKey, ...[...ids].sort()] : null;
  const { data, error } = useSWR(key, () => fetchByIds(ids), { keepPreviousData: true });

  useEffect(() => {
    if (!data) {
      return;
    }
    const resolved = new Set(data.map(getId));
    const orphans = ids.filter((id) => !resolved.has(id));
    if (orphans.length === 0) {
      return;
    }
    const orphanSet = new Set(orphans);
    updatePreferences((current) => {
      const stored = current.favoriteItems?.[favoritesKey] ?? [];
      return {
        favoriteItems: {
          ...current.favoriteItems,
          [favoritesKey]: stored.filter((id) => !orphanSet.has(id)),
        },
      };
    });
    // `ids`/`updatePreferences` are re-derived every render; keying the prune on
    // `data` alone runs it once per fetch result and converges after pruning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const toggleFavorite = (id: string) =>
    updatePreferences((current) => {
      const stored = current.favoriteItems?.[favoritesKey] ?? [];
      const next = stored.includes(id) ? stored.filter((f) => f !== id) : [...stored, id];
      return { favoriteItems: { ...current.favoriteItems, [favoritesKey]: next } };
    });

  // Intersect with the current favorite set so a just-unfavorited record (still
  // served by `keepPreviousData` until the refetch lands) drops immediately.
  const pinned = (data ?? []).filter((item) => favoriteSet.has(getId(item)));
  return {
    pinned,
    pinnedIds: new Set(pinned.map(getId)),
    isFavorite: (id: string) => favoriteSet.has(id),
    toggleFavorite,
    error,
  };
}
